import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { apiService } from '../services/apiService';

const UserContext = createContext();

// Convert numeric role to string
const convertRoleToString = (role) => {
  switch (role) {
    case 1:
      return 'User';
    case 2:
      return 'Technician';
    case 3:
      return 'Admin';
    default:
      return 'User';
  }
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        if (!authService.isAuthenticated()) {
          return;
        }

        const userId = authService.getUserId();
        if (!userId) {
          return;
        }

        // Prefer register response (canonical Id from API) over get-by-id races.
        const registered = await authService.ensureRegisteredInDatabase().catch(() => null);
        if (registered?.id) {
          setUser(registered);
          setUserRole(convertRoleToString(registered.role));
          return;
        }

        try {
          const userData = await apiService.getUserById(userId);
          setUser(userData);
          setUserRole(convertRoleToString(userData.role));
        } catch {
          setUser({
            id: userId,
            name: authService.getUserName(),
            email: authService.getUserEmail(),
            role: 'User',
          });
          setUserRole('User');
        }
      } catch {
        if (authService.isAuthenticated()) {
          setUser({
            id: authService.getUserId(),
            name: authService.getUserName(),
            email: authService.getUserEmail(),
            role: 'User',
          });
          setUserRole('User');
        }
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, []);

  const isAdmin = () => userRole === 'Admin';
  const isTechnician = () => userRole === 'Technician';
  const isAdminOrTechnician = () => isAdmin() || isTechnician();
  const isUser = () => userRole === 'User';

  const value = {
    user,
    userRole,
    loading,
    isAdmin,
    isTechnician,
    isAdminOrTechnician,
    isUser,
    setUser,
    setUserRole,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
